import { useCallback, useState, useEffect, useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';

// Custom import from In-house library
import { RuleBuilderSuggestionOptionSchema } from '@flybits/webapp-react-rule-builder';

// Services
import ContentAPI from 'services/api/content.api';
import ContextAPI from 'services/api/context.api';

// Interfaces
import { PaginationResponse } from 'interface/shared/api.interface';
import { Content, Component } from 'interface/content/content.interface';

type QueryError = {
  message: string;
};
type ContentInstanceType = RuleBuilderSuggestionOptionSchema[] | undefined;

const LIMIT = 30;

export default function useContentAnalytics(searchVal: string) {
  const contentAPI = useMemo(() => new ContentAPI(), []);

  const fetchContentInstances = useCallback(
    async ({ pageParam = 0 }) => {
      const instancesResult = await contentAPI.getContentInstances({
        limit: LIMIT,
        offset: pageParam,
        labelsFormula: '(!fb-auto-gen)', // we don't want to show auto-generated content instances
        search: searchVal,
        managementMode: true,
      });
      return instancesResult;
    },
    [contentAPI, searchVal],
  );

  const {
    isLoading,
    isFetchingNextPage,
    data: contentData,
    fetchNextPage: fetchNextContentPage,
    status,
    error,
  } = useInfiniteQuery<PaginationResponse<Content>, QueryError>({
    queryKey: ['content-instances', searchVal],
    queryFn: fetchContentInstances,
    getNextPageParam: (lastPage) =>
      lastPage.pagination.offset + lastPage.pagination.limit > lastPage.pagination.totalRecords
        ? undefined
        : lastPage.pagination.offset + lastPage.pagination.limit,
  });

  // Serializing Content data
  let contentInstances: ContentInstanceType = undefined;
  const isError = status === 'error';
  const serverErrorMessage = error?.message;

  if (contentData && !isError) {
    contentInstances = contentData.pages
      .flatMap((page) => page.data)
      .map((contentData) => {
        const { localizations, modifiedAt, id: contentDataId } = contentData;
        const name = localizations?.en?.name || '';
        const modifiedDate = new Date(modifiedAt || '').toDateString();
        const subText: string[] = [];

        if (contentDataId) {
          subText.push(contentDataId);
        }
        subText.push(modifiedDate);

        return {
          name,
          subText,
          payload: contentData,
        };
      });
  }

  return { isLoading, isFetchingNextPage, contentInstances, fetchNextContentPage, isError, serverErrorMessage };
}

export function useContentAnalyticsComponents(contentId: string, uid: string) {
  const contextAPI = useMemo(() => new ContextAPI(), []);

  const [options, setOptions] = useState([]);
  const [apiResponse, setApiResponse] = useState<
    PaginationResponse<Component> & { error: boolean; isLoading: boolean }
  >();
  const [isLoading, setIsLoading] = useState(false);

  const COMPONENT_ENGAGED = 'ctx.flybits.contentAnalytics.query.componentEngaged';

  const ctxVariable = uid === COMPONENT_ENGAGED ? `${uid}.${contentId}.*.*.*` : `${uid}.${contentId}.*`;

  const fetchComponents = useCallback(async () => {
    setIsLoading(true);

    const response = await contextAPI.getParameters(ctxVariable);

    setApiResponse(response);

    const responseDataOptions =
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      response.data?.map((option: any) => {
        return {
          name: uid === COMPONENT_ENGAGED ? option.componentName : option.componentID,
          subText: uid === COMPONENT_ENGAGED ? [option.componentType] : [],
          payload: option,
        };
      }) || [];
    if (responseDataOptions.length)
      responseDataOptions.unshift({
        name: 'Any Component',
        subText: [],
        payload: {
          componentID: '*',
          componentName: '*',
          componentType: '*',
        },
      });
    setOptions(responseDataOptions);
    setIsLoading(false);
  }, [contextAPI, ctxVariable, uid]);

  useEffect(() => {
    if (uid && contentId) fetchComponents();
  }, [fetchComponents, uid, contentId]);

  return { data: options, isError: apiResponse?.error || false, isLoading };
}
